// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Machine definition validation — the Schema, Bounds and reference-integrity classes of
// 04 §16. The geometry classes (containment, overlap, trap flood-fill, reachability) and the
// rule-graph termination analysis are M2-21 and M2-11; each has its own note below saying so,
// because a validator that silently covers less than its documentation claims is worse than
// one that covers nothing.
//
// **Unknown fields are rejected, not ignored.** This is the load-bearing rule of the whole
// file and the reason it is hand-written rather than delegated to a schema library the core
// may not depend on. A definition is authored by one player and loaded by another; if an
// unrecognised key is silently dropped, an author can write something one client version
// honours and another ignores, and the two then disagree about what the machine *is*. That is
// the same failure as a divergent simulation, arriving through the front door.
//
// Errors name the offending element by path — `colliders[7].normal`, not "invalid collider" —
// because the Builder shows this text to a player who has to fix it (04 §16).
//
// Everything here runs at load. None of it is on the hot path, so it is written for clarity
// and for the quality of its messages rather than for speed.

import { MATERIALS, MATERIAL_OVERRIDE_BOUNDS } from '../sim/materials'
import type { MaterialName } from '../sim/materials'
import {
  BALL_DIAMETER,
  MAX_BALLS,
  PLAYFIELD_HALF_LENGTH,
  PLAYFIELD_HALF_WIDTH,
} from '../sim/constants'
import {
  ACTION_NAMES,
  ART_OPS,
  COLLIDER_KINDS,
  DEVICE_KINDS,
  DISPLAY_OPS,
  EXPRESSION_OPERATORS,
  FORMAT_VERSION,
  OPERATOR_BALL_SAVE,
  SYNTH_NAMES,
} from './types'
import type { MachineDefinition } from './types'
import { analyseTermination } from '../rules/termination'
import { analyseGeometry } from './geometry'

export interface ValidationIssue {
  /** Dotted/indexed path to the offending element, e.g. `devices[3].pivot`. */
  readonly path: string
  /** Stable machine-readable code, so the Builder can offer a targeted fix. */
  readonly code: string
  readonly message: string
}

export interface ValidationReport {
  readonly ok: boolean
  readonly errors: readonly ValidationIssue[]
  /** Legal but probably a mistake. Never blocks a load. */
  readonly warnings: readonly ValidationIssue[]
}

/** Perpendicularity and unit-length tolerance for a supplied normal (04 §6). */
const NORMAL_TOLERANCE = 1e-6

/**
 * 04 §4 — outside this band the game stops being pinball.
 *
 * **Exported for the same reason `ART_MAX_OPS_PER_SURFACE` is** (M12-62): a surface that offers an
 * author a number the validator will refuse should refuse it first, with the band in the message,
 * and the alternative is that surface typing `5` and `8` again. `packages/pinball-mcp/src/layout.ts`
 * had its own pair, under a comment saying *"which is `validate.ts`'s `INCLINE_MIN`/`INCLINE_MAX`"*
 * — a claim about this file rather than a reference to it, and the kind that stays true only until
 * one of the two moves.
 */
export const INCLINE_MIN = 5
export const INCLINE_MAX = 8

/**
 * 04 §10 — a definition cannot ask for a frictionless playfield or a trampoline.
 *
 * Taken from the material table itself rather than restated, so that validation rejects
 * precisely what `applyOverrides` would otherwise clamp without telling anyone.
 */
const SCATTER_MAX_DEGREES = MATERIAL_OVERRIDE_BOUNDS.scatterDegrees[1]

/** 04 §11.3 — the evaluator's bounds. */
const EXPRESSION_MAX_DEPTH = 16
const EXPRESSION_MAX_NODES = 256

/**
 * 04 §16 "Art — total draw budget within limit".
 *
 * Art is executed onto a canvas at load, once, so the budget is about load time rather than
 * frame time: a thousand-op playfield would stall the machine select for seconds on a Quest.
 * Sixty-four operations is far more than NEON NOVA's playfield needs and still bounded.
 *
 * Exported because the Machine Builder shows the budget while an author is spending it, and a
 * second copy of the number in the editor is a number that can drift from the one that refuses.
 */
export const ART_MAX_OPS_PER_SURFACE = 64
export const ART_MAX_OPS_TOTAL = 256

/** A DMD frame is a list of generator ops; a scene with hundreds of them is a bitmap in disguise. */
const DISPLAY_MAX_OPS_PER_SCENE = 48

const MATERIAL_NAMES = Object.keys(MATERIALS) as MaterialName[]

const EVENT_PREFIXES = ['switch', 'var', 'mode', 'timer', 'ball']

import {wireSupportIds} from './wireSupports'
import {slingCoverIds} from './slingCovers'

class Ctx {
  readonly errors: ValidationIssue[] = []
  readonly warnings: ValidationIssue[] = []

  error(path: string, code: string, message: string): void {
    this.errors.push({ path, code, message })
  }

  warn(path: string, code: string, message: string): void {
    this.warnings.push({ path, code, message })
  }

  /**
   * Reject any key not in `allowed`, and require every key in `required`.
   *
   * Sorted before reporting so two runs over the same document produce byte-identical
   * output — a validator whose message order depends on object layout makes a diff between
   * two validation runs unreadable.
   */
  shape(path: string, value: unknown, required: readonly string[], optional: readonly string[]): boolean {
    if (!isPlainObject(value)) {
      this.error(path, 'not-an-object', `expected an object, found ${describe(value)}`)
      return false
    }
    const present = Object.keys(value).sort()
    const allowed = new Set([...required, ...optional])
    for (const key of present) {
      if (!allowed.has(key)) {
        this.error(
          `${path}.${key}`,
          'unknown-field',
          `unknown field "${key}"; a definition may not carry fields this build does not understand`,
        )
      }
    }
    for (const key of required) {
      if (!(key in value)) {
        this.error(path, 'missing-field', `required field "${key}" is absent`)
      }
    }
    return true
  }

  num(path: string, value: unknown, min: number, max: number): number | undefined {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      this.error(path, 'not-a-number', `expected a finite number, found ${describe(value)}`)
      return undefined
    }
    if (value < min || value > max) {
      this.error(path, 'out-of-range', `${value} is outside the permitted range ${min}…${max}`)
      return undefined
    }
    return value
  }

  int(path: string, value: unknown, min: number, max: number): number | undefined {
    const n = this.num(path, value, min, max)
    if (n === undefined) return undefined
    if (!Number.isInteger(n)) {
      this.error(path, 'not-an-integer', `expected a whole number, found ${n}`)
      return undefined
    }
    return n
  }

  /**
   * A flag, and only a flag.
   *
   * Added in M9-37 with the match block, and three older call sites had already hand-rolled it —
   * `not-a-boolean` appears in `validate.ts` three times with three different sentences. This is
   * the one every new caller gets, so the fourth is not a fourth sentence.
   */
  bool(path: string, value: unknown): boolean | undefined {
    if (typeof value !== 'boolean') {
      this.error(path, 'not-a-boolean', `expected true or false, found ${describe(value)}`)
      return undefined
    }
    return value
  }

  str(path: string, value: unknown): string | undefined {
    if (typeof value !== 'string' || value.length === 0) {
      this.error(path, 'not-a-string', `expected a non-empty string, found ${describe(value)}`)
      return undefined
    }
    return value
  }

  oneOf<T extends string>(path: string, value: unknown, options: readonly T[]): T | undefined {
    if (typeof value !== 'string' || !options.includes(value as T)) {
      this.error(
        path,
        'not-in-vocabulary',
        `${describe(value)} is not one of: ${options.join(', ')}`,
      )
      return undefined
    }
    return value as T
  }

  point(path: string, value: unknown): readonly [number, number] | undefined {
    if (!Array.isArray(value) || value.length !== 2) {
      this.error(path, 'not-a-point', `expected [x, y], found ${describe(value)}`)
      return undefined
    }
    const x = this.num(`${path}[0]`, value[0], -10, 10)
    const y = this.num(`${path}[1]`, value[1], -10, 10)
    if (x === undefined || y === undefined) return undefined
    return [x, y]
  }

  array(path: string, value: unknown, min: number): unknown[] | undefined {
    if (!Array.isArray(value)) {
      this.error(path, 'not-an-array', `expected an array, found ${describe(value)}`)
      return undefined
    }
    if (value.length < min) {
      this.error(path, 'too-short', `expected at least ${min} entr${min === 1 ? 'y' : 'ies'}, found ${value.length}`)
      return undefined
    }
    return value
  }
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

function describe(v: unknown): string {
  if (v === null) return 'null'
  if (Array.isArray(v)) return `an array of ${v.length}`
  if (typeof v === 'string') return `"${v}"`
  return String(v)
}

/** Collect duplicate ids across a named collection, naming both offenders. */
function collectIds(ctx: Ctx, path: string, items: unknown[], seen: Map<string, string>): string[] {
  const ids: string[] = []
  for (let i = 0; i < items.length; i++) {
    const item = items[i]
    const id = isPlainObject(item) && typeof item['id'] === 'string' ? item['id'] : undefined
    if (id === undefined) {
      ctx.error(`${path}[${i}].id`, 'missing-id', 'every element needs a unique string id')
      ids.push('')
      continue
    }
    const previous = seen.get(id)
    if (previous !== undefined) {
      ctx.error(`${path}[${i}].id`, 'duplicate-id', `id "${id}" is already used by ${previous}`)
    } else {
      seen.set(id, `${path}[${i}]`)
    }
    ids.push(id)
  }
  return ids
}

/**
 * Validate a supplied `normal` against the face it belongs to (04 §6).
 *
 * Both checks matter and neither is fussiness. A non-unit normal scales every impulse through
 * that surface; a normal that is not perpendicular to its own face deflects consistently to
 * one side. Neither looks like a bug — the machine simply plays slightly wrong, everywhere,
 * for ever. This document itself carried `(0.714, 0.700)`, which is out by 1.3e-3.
 */
function checkNormal(
  ctx: Ctx,
  path: string,
  normal: readonly [number, number],
  a: readonly [number, number],
  b: readonly [number, number],
): void {
  const len = Math.sqrt(normal[0] * normal[0] + normal[1] * normal[1])
  if (Math.abs(len - 1) > NORMAL_TOLERANCE) {
    ctx.error(path, 'normal-not-unit', `normal has length ${len}, expected 1 within ${NORMAL_TOLERANCE}`)
  }
  const dx = b[0] - a[0]
  const dy = b[1] - a[1]
  const faceLen = Math.sqrt(dx * dx + dy * dy)
  if (faceLen === 0) {
    ctx.error(path, 'degenerate-face', 'a and b are the same point, so the face has no direction')
    return
  }
  const dot = (normal[0] * dx + normal[1] * dy) / faceLen
  if (Math.abs(dot) > NORMAL_TOLERANCE) {
    ctx.error(
      path,
      'normal-not-perpendicular',
      `normal is out of perpendicular by ${Math.abs(dot)}, tolerance ${NORMAL_TOLERANCE}. ` +
        'Order the endpoints anticlockwise and omit the normal instead — the left-hand ' +
        'perpendicular of a → b is then correct by construction.',
    )
  }
}

// --- expressions -------------------------------------------------------------

interface ExprBudget {
  nodes: number
}

/**
 * Walk an expression tree, enforcing the closed operator list and the evaluator's bounds.
 *
 * The depth and node limits are not performance tuning — they are what makes the evaluator
 * *bounded*, and therefore what makes running a stranger's rules safe (SEC-012). An
 * unbounded tree is a denial of service that any player could publish.
 */
function checkExpression(ctx: Ctx, path: string, node: unknown, depth: number, budget: ExprBudget): void {
  budget.nodes++
  if (budget.nodes > EXPRESSION_MAX_NODES) {
    ctx.error(path, 'expression-too-large', `expression exceeds the ${EXPRESSION_MAX_NODES}-node budget`)
    return
  }
  if (depth > EXPRESSION_MAX_DEPTH) {
    ctx.error(path, 'expression-too-deep', `expression is deeper than the limit of ${EXPRESSION_MAX_DEPTH}`)
    return
  }
  if (typeof node === 'number' || typeof node === 'boolean') return
  if (typeof node === 'string') {
    ctx.error(path, 'expression-string', 'expressions carry no strings; use a set member or a var reference')
    return
  }
  if (!isPlainObject(node)) {
    ctx.error(path, 'expression-malformed', `expected a literal or an operator object, found ${describe(node)}`)
    return
  }
  const keys = Object.keys(node)
  if (keys.length !== 1) {
    ctx.error(
      path,
      'expression-malformed',
      `an operator object carries exactly one key, found ${keys.length}: ${keys.sort().join(', ')}`,
    )
    return
  }
  const op = keys[0]!
  if (!(EXPRESSION_OPERATORS as readonly string[]).includes(op)) {
    ctx.error(
      path,
      'unknown-operator',
      `"${op}" is not an operator. The complete list is: ${EXPRESSION_OPERATORS.join(', ')}`,
    )
    return
  }
  const arg = node[op]
  // `var`, `modeActive`, `timerRunning` take a name; the rest take an operand or a list.
  if (op === 'var' || op === 'modeActive' || op === 'timerRunning' || op === 'switchClosed' || op === 'lampState') {
    if (typeof arg !== 'string' || arg.length === 0) {
      ctx.error(`${path}.${op}`, 'expression-malformed', `${op} takes a name, found ${describe(arg)}`)
    }
    return
  }
  // Two set queries take a shape of their own, because the cardinality and membership of a
  // bitmask are not things the arithmetic operators could express.
  if (op === 'count') {
    if (!isPlainObject(arg) || typeof arg['var'] !== 'string') {
      ctx.error(`${path}.count`, 'expression-malformed', 'count takes { "var": "<setId>" }')
    }
    return
  }
  if (op === 'has') {
    if (!isPlainObject(arg) || typeof arg['var'] !== 'string' || typeof arg['member'] !== 'string') {
      ctx.error(`${path}.has`, 'expression-malformed', 'has takes { "var": "<setId>", "member": "<name>" }')
    }
    return
  }
  if (op === 'ballsInPlay' || op === 'playerBall') return
  if (Array.isArray(arg)) {
    for (let i = 0; i < arg.length; i++) checkExpression(ctx, `${path}.${op}[${i}]`, arg[i], depth + 1, budget)
    return
  }
  checkExpression(ctx, `${path}.${op}`, arg, depth + 1, budget)
}

/**
 * Everything an action can name, so "Rules — references" covers the whole graph.
 *
 * Collected while walking, because the same walk answers "Rules — reachability": a mode is
 * startable only if something, somewhere, carries `startMode` for it.
 */
interface ActionScope {
  readonly refs: RuleRefs
  readonly varIds: Set<string>
  readonly timerIds: Set<string>
  readonly modeIds: Set<string>
  readonly lampGroups: Set<string>
  /** Filled as the walk proceeds. */
  readonly started: Set<string>
  readonly ended: Set<string>
  /**
   * Every lamp and lamp group a rule addresses, filled as the walk proceeds (M9-40).
   *
   * **The same walk that proves a mode is startable proves a lamp is lightable**, which is why
   * this is collected here rather than by a second pass: an insert nothing ever addresses is dark
   * for the life of the machine, and the only sign is a bulb that never comes on —
   * indistinguishable, from inside the game, from a rule that never fires.
   *
   * M9-39 found **sixteen** of them across the two shipped machines, by hand, after a sweep. This
   * is what stops the seventeenth.
   */
  readonly lampsLit: Set<string>
  readonly groupsLit: Set<string>
}

function checkActions(ctx: Ctx, path: string, actions: unknown, scope: ActionScope): void {
  const list = ctx.array(path, actions, 0)
  if (!list) return
  for (let i = 0; i < list.length; i++) {
    const action = list[i]
    if (!isPlainObject(action)) {
      ctx.error(`${path}[${i}]`, 'action-malformed', `expected an action object, found ${describe(action)}`)
      continue
    }
    const keys = Object.keys(action).sort()
    if (keys.length === 0) {
      ctx.error(`${path}[${i}]`, 'action-malformed', 'an action object cannot be empty')
      continue
    }
    for (const key of keys) {
      // `award` carries a sibling modifier; everything else is a single-keyed action.
      if (key === 'multiplyByBonusX' && keys.includes('award')) continue
      if (!(ACTION_NAMES as readonly string[]).includes(key)) {
        ctx.error(
          `${path}[${i}].${key}`,
          'unknown-action',
          `"${key}" is not an action. An unrecognised action is refused at load rather than ` +
            `ignored at runtime, so that two clients cannot disagree about what a rule does. ` +
            `The complete list is: ${ACTION_NAMES.join(', ')}`,
        )
        continue
      }
      checkActionRefs(ctx, `${path}[${i}]`, key, action[key], scope)
    }
  }
}

/** One action's references. Nothing here is optional: a dangling name is a rule that does nothing. */
function checkActionRefs(ctx: Ctx, path: string, key: string, arg: unknown, scope: ActionScope): void {
  const named = (what: string, id: unknown, known: Set<string>, code: string): void => {
    const name = ctx.str(`${path}.${key}`, id)
    if (name !== undefined && !known.has(name)) {
      ctx.error(`${path}.${key}`, code, `no ${what} is declared with id "${name}"`)
    }
  }

  switch (key) {
    case 'set':
    case 'add': {
      if (!isPlainObject(arg)) return
      named('variable', arg['var'], scope.varIds, 'unknown-var')
      // `to` and `amount` may be expressions, which is what makes a doubling super jackpot
      // authorable. They run in the same bounded evaluator as a `when`, so they get the same
      // budget check — an unbounded tree here would be exactly the denial of service the
      // depth limit exists to prevent (SEC-012).
      for (const field of ['to', 'amount'] as const) {
        const value = arg[field]
        if (value === undefined || typeof value === 'number' || typeof value === 'boolean') continue
        if (field === 'to' && value === 'empty') continue
        if (field === 'amount' && typeof arg['member'] === 'string') continue
        checkExpression(ctx, `${path}.${key}.${field}`, value, 1, { nodes: 0 })
      }
      return
    }
    case 'award': {
      if (typeof arg === 'number') return
      checkExpression(ctx, `${path}.award`, arg, 1, { nodes: 0 })
      return
    }
    case 'lamp': {
      if (!isPlainObject(arg)) return
      named('lamp', arg['id'], scope.refs.lampIds, 'unknown-lamp')
      if (typeof arg['id'] === 'string') scope.lampsLit.add(arg['id'])
      return
    }
    case 'lampGroup': {
      if (!isPlainObject(arg)) return
      named('lamp group', arg['id'], scope.lampGroups, 'unknown-lamp-group')
      if (typeof arg['id'] === 'string') scope.groupsLit.add(arg['id'])
      return
    }
    case 'coil': {
      if (!isPlainObject(arg)) return
      named('coil', arg['id'], scope.refs.coilIds, 'unknown-coil')
      if (arg['state'] !== undefined) {
        ctx.oneOf(`${path}.coil.state`, arg['state'], ['pulse', 'enable', 'disable'])
      }
      return
    }
    case 'startTimer':
    case 'stopTimer':
      named('timer', arg, scope.timerIds, 'unknown-timer')
      return
    case 'startMode': {
      named('mode', arg, scope.modeIds, 'unknown-mode')
      if (typeof arg === 'string') scope.started.add(arg)
      return
    }
    case 'endMode': {
      const id = typeof arg === 'string' ? arg : isPlainObject(arg) && typeof arg['id'] === 'string' ? arg['id'] : undefined
      if (id === undefined) {
        ctx.error(`${path}.endMode`, 'action-malformed', 'endMode takes a mode id')
        return
      }
      if (!scope.modeIds.has(id)) ctx.error(`${path}.endMode`, 'unknown-mode', `no mode is declared with id "${id}"`)
      scope.ended.add(id)
      return
    }
    case 'lockBall': {
      if (!isPlainObject(arg)) return
      named('device', arg['device'], scope.refs.deviceIds, 'unknown-device')
      return
    }
    case 'enableBallSave': {
      // **The one action argument that names an operator setting** (M9-35, 04 §11.4). A number is
      // milliseconds and always has been; `"operator"` defers to 04 §15's `ballSaveSeconds`.
      //
      // Both refusals below exist because the failure is otherwise *silent and looks correct*: a
      // string this action does not understand fell through to `0` in both cores, so `"Operator"`,
      // `"operators"` or `"12000"` produced a rule that granted a nought-second ball save while
      // reading, in the document, as one that granted a save. The same for a machine that asks for
      // the operator's number and declares no operator menu to hold one.
      if (typeof arg === 'number') return
      if (arg !== OPERATOR_BALL_SAVE) {
        ctx.error(
          `${path}.enableBallSave`,
          'action-malformed',
          `enableBallSave takes a duration in milliseconds or the string "${OPERATOR_BALL_SAVE}", ` +
            `found ${describe(arg)}`,
        )
        return
      }
      if (!scope.refs.hasOperatorBallSave) {
        ctx.error(
          `${path}.enableBallSave`,
          'unknown-setting',
          `this rule defers to the operator's ball save, and the machine declares no ` +
            `"ballSaveSeconds" setting for one to come from. Add it to the settings block, or ` +
            `name a duration in milliseconds here.`,
        )
      }
      return
    }
    case 'display': {
      if (!isPlainObject(arg) || !scope.refs.hasDisplay) return
      const scene = ctx.str(`${path}.display`, arg['scene'])
      if (scene !== undefined && !scope.refs.sceneIds.has(scene)) {
        ctx.error(`${path}.display`, 'unknown-scene', `no display scene is declared with id "${scene}"`)
      }
      return
    }
    case 'sound': {
      if (!isPlainObject(arg) || !scope.refs.hasAudio) return
      const cue = ctx.str(`${path}.sound`, arg['cue'])
      if (cue !== undefined && !scope.refs.cueIds.has(cue)) {
        ctx.error(`${path}.sound`, 'unknown-cue', `no audio cue is declared with id "${cue}"`)
      }
      return
    }
    case 'speech': {
      if (!isPlainObject(arg) || !scope.refs.hasAudio) return
      const line = ctx.str(`${path}.speech`, arg['cue'])
      if (line !== undefined && !scope.refs.speechIds.has(line)) {
        ctx.error(`${path}.speech`, 'unknown-speech', `no speech line is declared with id "${line}"`)
      }
      return
    }
    default:
      return
  }
}

// --- the document ------------------------------------------------------------

export interface ValidateOptions {
  /**
   * Run the flood-fill geometry classes (trap, device reachability).
   *
   * Off by default. They rasterise the whole playfield at ball radius, which is milliseconds
   * rather than microseconds — right for the Builder, for the server's publish check and for
   * the test suite, wrong for a client loading a machine the server already cleared.
   */
  readonly flood?: boolean
}

/**
 * Validate a parsed machine definition.
 *
 * Takes `unknown` rather than a typed definition on purpose: the input is a stranger's JSON,
 * and a signature claiming otherwise would be a lie that TypeScript happily believes.
 */
export function validateMachine(doc: unknown, options: ValidateOptions = {}): ValidationReport {
  const ctx = new Ctx()

  const ok = ctx.shape(
    '',
    doc,
    ['formatVersion', 'machineId', 'machineVersion', 'meta', 'dimensions', 'layers', 'colliders', 'devices'],
    ['regions', 'lamps', 'switches', 'coils', 'toys', 'physics', 'rules', 'display', 'art', 'audio', 'settings'],
  )
  if (!ok) return { ok: false, errors: ctx.errors, warnings: ctx.warnings }
  const m = doc as Record<string, unknown>

  // --- versioning (04 §17) ---------------------------------------------------
  const formatVersion = ctx.str('formatVersion', m['formatVersion'])
  if (formatVersion !== undefined) {
    const major = formatVersion.split('.')[0]
    const ourMajor = FORMAT_VERSION.split('.')[0]
    if (major !== ourMajor) {
      ctx.error(
        'formatVersion',
        'format-version-unsupported',
        `this build reads format ${ourMajor}.x; the definition declares ${formatVersion}`,
      )
    }
  }
  ctx.str('machineId', m['machineId'])
  const machineVersion = ctx.str('machineVersion', m['machineVersion'])
  if (machineVersion !== undefined && !/^\d+\.\d+\.\d+$/.test(machineVersion)) {
    ctx.error('machineVersion', 'not-semver', `"${machineVersion}" is not a semver triple`)
  }

  validateMeta(ctx, m['meta'])
  const dims = validateDimensions(ctx, m['dimensions'])

  // --- identifier namespaces -------------------------------------------------
  // Layers, colliders and devices share one namespace because colliders and devices refer to
  // each other by bare id; a collider named the same as a device makes `device: "x"` ambiguous.
  const layerList = ctx.array('layers', m['layers'], 1) ?? []
  const colliderList = ctx.array('colliders', m['colliders'], 0) ?? []
  const deviceList = ctx.array('devices', m['devices'], 0) ?? []
  const lampList = ctx.array('lamps', m['lamps'] ?? [], 0) ?? []
  const switchList = ctx.array('switches', m['switches'] ?? [], 0) ?? []
  const coilList = ctx.array('coils', m['coils'] ?? [], 0) ?? []

  const geometryIds = new Map<string, string>()
  const layerIds = new Set(collectIds(ctx, 'layers', layerList, geometryIds))
  collectIds(ctx, 'colliders', colliderList, geometryIds)
  const deviceIds = new Set(collectIds(ctx, 'devices', deviceList, geometryIds))

  const lampIds = new Set(collectIds(ctx, 'lamps', lampList, new Map()))
  const switchIds = new Set(collectIds(ctx, 'switches', switchList, new Map()))
  const coilIds = new Set(collectIds(ctx, 'coils', coilList, new Map()))

  // A ramp and a habitrail are declared as layers but become devices at load — the loader
  // builds a `Ramp`/`WireGuide` for each and registers it under the layer's id. So anything
  // that names a device may name one of those too, or a ramp could not have a switch.
  const simDeviceIds = new Set(deviceIds)
  for (const layer of layerList) {
    if (!isPlainObject(layer)) continue
    if (layer['kind'] !== 'ramp' && layer['kind'] !== 'wire') continue
    if (typeof layer['id'] === 'string') simDeviceIds.add(layer['id'])
  }

  validateLayers(ctx, layerList, layerIds, dims)
  // Read before the colliders are walked, because a collider marked as outlane geometry with
  // no machine-wide scatter to apply is worth a warning at the collider, not at the physics
  // block a hundred lines away.
  const physicsBlock = isPlainObject(m['physics']) ? m['physics'] : undefined
  const outlaneScatter =
    typeof physicsBlock?.['outlaneScatterDegrees'] === 'number'
      ? (physicsBlock['outlaneScatterDegrees'] as number)
      : undefined
  validateColliders(ctx, colliderList, layerIds, simDeviceIds, dims, outlaneScatter, m['settings'])
  const dropTargetIds = validateDevices(ctx, deviceList, layerIds, deviceIds, lampIds, switchIds, coilIds, dims)
  validateDropTargetBanks(ctx, deviceList, dropTargetIds)
  const regionIds = validateRegions(ctx, m['regions'], dims)
  const lampGroups = validateLamps(ctx, lampList, regionIds)
  validateSwitches(ctx, switchList, simDeviceIds)
  validateCoils(ctx, coilList)
  if (m['physics'] !== undefined) validatePhysics(ctx, m['physics'], troughCapacityOf(m))

  // The presentation blocks are validated before the rules, because a rule naming a scene, a
  // cue or a speech line is checked against the sets these produce.
  const sceneIds = m['display'] !== undefined ? validateDisplay(ctx, m['display']) : new Set<string>()
  if (m['art'] !== undefined) validateArt(ctx, m['art'])
  const audio = m['audio'] !== undefined ? validateAudio(ctx, m['audio']) : { cues: new Set<string>(), speech: new Set<string>() }

  let ruleNames: RuleNames = {
    varIds: new Set(),
    modeIds: new Set(),
    lampsLit: new Set(),
    groupsLit: new Set(),
  }
  if (m['rules'] !== undefined) {
    ruleNames = validateRules(ctx, m['rules'], {
      deviceIds: simDeviceIds,
      lampIds,
      switchIds,
      coilIds,
      lampGroups,
      sceneIds,
      cueIds: audio.cues,
      speechIds: audio.speech,
      hasDisplay: m['display'] !== undefined,
      hasAudio: m['audio'] !== undefined,
      hasOperatorBallSave: isPlainObject(m['settings']) && 'ballSaveSeconds' in m['settings'],
    })
    // Only worth running once every reference resolves — the graph walk assumes the ids it
    // follows exist, and a cascade report over a rulesheet with dangling names is noise on
    // top of the real error.
    if (ctx.errors.length === 0) {
      for (const issue of analyseTermination(doc as MachineDefinition)) {
        if (issue.severity === 'error') ctx.error(issue.path, issue.code, issue.message)
        else ctx.warn(issue.path, issue.code, issue.message)
      }
    }
  }

  // 04 §16, and M9-39's sixteen dark inserts (M9-40).
  //
  // **A lamp nothing addresses is dark for the life of the machine**, and the only sign of it is a
  // bulb that never comes on — which from inside the game is indistinguishable from a rule that
  // never fires. M9-39 found *sixteen* of them across the two shipped machines by sweeping the
  // definitions by hand: all six of NEON NOVA's Star System inserts, its `shootAgain`, its right
  // inlane arrow, all three flashers, and five more on IRON FATHOM within a minute of a guard
  // existing. This is the guard.
  //
  // **A warning, not an error.** An unlit lamp is a machine that plays perfectly and looks
  // slightly less alive than its author meant; refusing to load it would refuse a machine
  // mid-edit, which the Builder produces constantly.
  //
  // Three ways to be lit, and missing any of them would make this fire on correct machines: a rule
  // that names the lamp, a rule that names its *group*, or a device that carries it — a pop bumper
  // declaring `lamp` lights its own ring with no rule anywhere.
  checkLampsAreLit(ctx, lampList, deviceList, ruleNames)

  // After the rules, because every toy names a variable or a mode the rulesheet declares.
  if (m['toys'] !== undefined) {
    validateToys(ctx, m['toys'], layerIds, geometryIds, ruleNames)
  }
  if (m['settings'] !== undefined) validateSettings(ctx, m['settings'])

  // Geometry last, and only over a document whose shapes are known good: every pass in
  // `geometry.ts` reads typed fields directly, and running it over a half-valid document
  // would report a cascade of nonsense on top of the real error.
  if (ctx.errors.length === 0) {
    for (const issue of analyseGeometry(doc as MachineDefinition, { flood: options.flood === true })) {
      if (issue.severity === 'error') ctx.error(issue.path, issue.code, issue.message)
      else ctx.warn(issue.path, issue.code, issue.message)
    }
  }

  return { ok: ctx.errors.length === 0, errors: ctx.errors, warnings: ctx.warnings }
}

// --- §12 display, §13 art, §14 audio ------------------------------------------------
//
// Three classes of 04 §16 that all have the same shape: a closed vocabulary of operations,
// and a set of names the rules are then checked against. An op outside the vocabulary is
// refused for exactly the reason an unknown action is — one client would draw it and another
// would not, and the two would disagree about what the machine looks like.
//
// **What is deliberately not checked**: the *parameters* of an op. Each op has its own, and
// enumerating all of them belongs with the renderer that consumes them (M3). The closed op
// list is the part that carries the sandbox guarantee; the parameters are numbers and colours
// handed to a canvas.

function validateDisplay(ctx: Ctx, display: unknown): Set<string> {
  const ids = new Set<string>()
  if (!ctx.shape('display', display, ['kind', 'width', 'height', 'scenes'], ['palette'])) return ids
  const d = display as Record<string, unknown>
  ctx.oneOf('display.kind', d['kind'], ['dmd', 'lcd', 'segment'])
  ctx.int('display.width', d['width'], 8, 1920)
  ctx.int('display.height', d['height'], 8, 1080)
  if (d['palette'] !== undefined) ctx.oneOf('display.palette', d['palette'], ['amber', 'red', 'green', 'white'])

  const scenes = ctx.array('display.scenes', d['scenes'], 1)
  if (!scenes) return ids
  for (let i = 0; i < scenes.length; i++) {
    const path = `display.scenes[${i}]`
    if (!ctx.shape(path, scenes[i], ['id'], ['kind', 'loop', 'durationMs', 'layout', 'frames'])) continue
    const scene = scenes[i] as Record<string, unknown>
    const id = ctx.str(`${path}.id`, scene['id'])
    if (id !== undefined) {
      if (ids.has(id)) ctx.error(`${path}.id`, 'duplicate-id', `scene "${id}" is declared twice`)
      ids.add(id)
    }
    if (scene['kind'] !== undefined) ctx.oneOf(`${path}.kind`, scene['kind'], ['score', 'frames', 'entry'])
    if (scene['durationMs'] !== undefined) ctx.num(`${path}.durationMs`, scene['durationMs'], 1, 600000)
    if (scene['layout'] !== undefined) {
      ctx.oneOf(`${path}.layout`, scene['layout'], ['onePlayer', 'twoPlayer', 'fourPlayer'])
    }
    if (scene['frames'] === undefined) continue
    const frames = ctx.array(`${path}.frames`, scene['frames'], 0)
    if (!frames) continue
    if (frames.length > DISPLAY_MAX_OPS_PER_SCENE) {
      ctx.error(
        `${path}.frames`,
        'display-over-budget',
        `${frames.length} operations exceeds the ${DISPLAY_MAX_OPS_PER_SCENE} a scene may carry`,
      )
    }
    for (let f = 0; f < frames.length; f++) {
      const frame = frames[f]
      if (!isPlainObject(frame) || typeof frame['op'] !== 'string') {
        ctx.error(`${path}.frames[${f}]`, 'op-malformed', `expected { "op": … }, found ${describe(frame)}`)
        continue
      }
      if (!(DISPLAY_OPS as readonly string[]).includes(frame['op'])) {
        ctx.error(
          `${path}.frames[${f}].op`,
          'unknown-display-op',
          `"${frame['op']}" is not a DMD operation. The complete list is: ${DISPLAY_OPS.join(', ')}`,
        )
      }
    }
  }
  return ids
}

function validateArt(ctx: Ctx, art: unknown): void {
  if (!ctx.shape('art', art, ['seed'], ['playfield', 'backglass', 'cabinet', 'plastics'])) return
  const a = art as Record<string, unknown>
  // 04 §13: "every op is deterministic given its seed", which needs the seed to exist.
  ctx.int('art.seed', a['seed'], 0, 2 ** 31 - 1)

  let total = 0
  for (const key of ['playfield', 'backglass', 'plastics'] as const) {
    if (a[key] === undefined) continue
    total += validateArtSurface(ctx, `art.${key}`, a[key])
  }
  if (a['cabinet'] !== undefined) {
    if (isPlainObject(a['cabinet'])) {
      const cab = a['cabinet']
      for (const face of Object.keys(cab).sort()) {
        total += validateArtSurface(ctx, `art.cabinet.${face}`, cab[face])
      }
    } else {
      ctx.error('art.cabinet', 'not-an-object', 'expected a map of named cabinet surfaces')
    }
  }
  if (total > ART_MAX_OPS_TOTAL) {
    ctx.error(
      'art',
      'art-over-budget',
      `${total} drawing operations across the machine exceeds the limit of ${ART_MAX_OPS_TOTAL}`,
    )
  }
}

function validateArtSurface(ctx: Ctx, path: string, surface: unknown): number {
  if (!ctx.shape(path, surface, ['program'], ['resolution'])) return 0
  const s = surface as Record<string, unknown>
  if (s['resolution'] !== undefined) {
    const r = s['resolution']
    if (!Array.isArray(r) || r.length !== 2) {
      ctx.error(`${path}.resolution`, 'not-a-point', 'expected [width, height] in pixels')
    } else {
      ctx.int(`${path}.resolution[0]`, r[0], 16, 4096)
      ctx.int(`${path}.resolution[1]`, r[1], 16, 4096)
    }
  }
  const program = ctx.array(`${path}.program`, s['program'], 1)
  if (!program) return 0
  if (program.length > ART_MAX_OPS_PER_SURFACE) {
    ctx.error(
      `${path}.program`,
      'art-over-budget',
      `${program.length} operations exceeds the ${ART_MAX_OPS_PER_SURFACE} one surface may carry`,
    )
  }
  for (let i = 0; i < program.length; i++) {
    const op = program[i]
    if (!isPlainObject(op) || typeof op['op'] !== 'string') {
      ctx.error(`${path}.program[${i}]`, 'op-malformed', `expected { "op": … }, found ${describe(op)}`)
      continue
    }
    if (!(ART_OPS as readonly string[]).includes(op['op'])) {
      ctx.error(
        `${path}.program[${i}].op`,
        'unknown-art-op',
        `"${op['op']}" is not a drawing operation. The complete list is: ${ART_OPS.join(', ')}`,
      )
    }
  }
  return program.length
}

function validateAudio(ctx: Ctx, audio: unknown): { cues: Set<string>; speech: Set<string> } {
  const cues = new Set<string>()
  const speech = new Set<string>()
  if (!ctx.shape('audio', audio, [], ['voice', 'music', 'cues', 'speech'])) return { cues, speech }
  const a = audio as Record<string, unknown>

  if (a['voice'] !== undefined && ctx.shape('audio.voice', a['voice'], ['kind', 'profile'], ['pitch'])) {
    const v = a['voice'] as Record<string, unknown>
    ctx.oneOf('audio.voice.kind', v['kind'], ['formant'])
    ctx.str('audio.voice.profile', v['profile'])
    if (v['pitch'] !== undefined) ctx.num('audio.voice.pitch', v['pitch'], 0.5, 2)
  }
  if (a['music'] !== undefined && ctx.shape('audio.music', a['music'], ['kind', 'program'], [])) {
    const mu = a['music'] as Record<string, unknown>
    ctx.oneOf('audio.music.kind', mu['kind'], ['sequencer'])
    ctx.str('audio.music.program', mu['program'])
  }

  if (a['cues'] !== undefined) {
    if (!isPlainObject(a['cues'])) {
      ctx.error('audio.cues', 'not-an-object', 'expected a map of cue name → cue')
    } else {
      const map = a['cues']
      for (const name of Object.keys(map).sort()) {
        cues.add(name)
        const path = `audio.cues.${name}`
        if (!ctx.shape(path, map[name], ['synth'], ['pitch', 'gain', 'spatial', 'notes'])) continue
        const cue = map[name] as Record<string, unknown>
        // The one check that matters: a cue naming an unknown synth has no recipe, so it plays
        // silence while the machine loads and scores perfectly — the hardest kind of bug to see.
        ctx.oneOf(`${path}.synth`, cue['synth'], SYNTH_NAMES)
        if (cue['pitch'] !== undefined) ctx.num(`${path}.pitch`, cue['pitch'], 0.25, 4)
        if (cue['gain'] !== undefined) ctx.num(`${path}.gain`, cue['gain'], 0, 2)
        if (cue['spatial'] !== undefined) ctx.oneOf(`${path}.spatial`, cue['spatial'], ['device', 'cabinet', 'backbox', 'none'])
      }
    }
  }

  if (a['speech'] !== undefined) {
    if (!isPlainObject(a['speech'])) {
      ctx.error('audio.speech', 'not-an-object', 'expected a map of line name → line')
    } else {
      const map = a['speech']
      for (const name of Object.keys(map).sort()) {
        speech.add(name)
        const path = `audio.speech.${name}`
        // 07 §4 requires a plain-text caption alongside the phonemes for NFR-022.
        if (!ctx.shape(path, map[name], ['text'], ['phonemes', 'emphasis'])) continue
        ctx.str(`${path}.text`, (map[name] as Record<string, unknown>)['text'])
      }
    }
  }
  return { cues, speech }
}

function validateMeta(ctx: Ctx, meta: unknown): void {
  if (!ctx.shape(
    'meta',
    meta,
    ['title', 'era', 'author', 'difficulty', 'playersMax', 'ballsPerGame', 'contentRating'],
    ['subtitle', 'estimatedBallTimeSeconds'],
  )) return
  const v = meta as Record<string, unknown>
  ctx.str('meta.title', v['title'])
  ctx.oneOf('meta.era', v['era'], ['em-1970s', 'ss-1980s', 'dmd-1990s', 'lcd-modern'])
  ctx.int('meta.difficulty', v['difficulty'], 1, 5)
  ctx.int('meta.playersMax', v['playersMax'], 1, 4)
  ctx.int('meta.ballsPerGame', v['ballsPerGame'], 1, 5)
  ctx.oneOf('meta.contentRating', v['contentRating'], ['everyone', 'teen'])
  if (ctx.shape('meta.author', v['author'], ['kind', 'displayName'], [])) {
    const a = v['author'] as Record<string, unknown>
    ctx.oneOf('meta.author.kind', a['kind'], ['first-party', 'player'])
    ctx.str('meta.author.displayName', a['displayName'])
  }
}

interface Dims {
  halfWidth: number
  halfLength: number
}

function validateDimensions(ctx: Ctx, dimensions: unknown): Dims {
  const fallback: Dims = { halfWidth: PLAYFIELD_HALF_WIDTH, halfLength: PLAYFIELD_HALF_LENGTH }
  if (!ctx.shape(
    'dimensions',
    dimensions,
    ['playfield', 'inclineDegrees', 'glassHeight'],
    ['cabinet', 'lockdownBarHeight', 'playfieldRecess'],
  )) return fallback
  const v = dimensions as Record<string, unknown>
  if(v['playfieldRecess']!==undefined)ctx.num('dimensions.playfieldRecess',v['playfieldRecess'],.005,.15)

  let half = fallback
  if (ctx.shape('dimensions.playfield', v['playfield'], ['width', 'length'], [])) {
    const p = v['playfield'] as Record<string, unknown>
    const w = ctx.num('dimensions.playfield.width', p['width'], 0.2, 1.0)
    const l = ctx.num('dimensions.playfield.length', p['length'], 0.4, 2.0)
    if (w !== undefined && l !== undefined) half = { halfWidth: w / 2, halfLength: l / 2 }
  }

  ctx.num('dimensions.inclineDegrees', v['inclineDegrees'], INCLINE_MIN, INCLINE_MAX)

  // The check that a full green suite once failed to make. See constants.ts GLASS_HEIGHT.
  const glass = ctx.num('dimensions.glassHeight', v['glassHeight'], 0, 0.4)
  if (glass !== undefined && glass <= BALL_DIAMETER) {
    ctx.error(
      'dimensions.glassHeight',
      'glass-below-ball',
      `glass at ${glass} m is not above the ball's own ${BALL_DIAMETER} m diameter, so the ball ` +
        'does not fit in the machine. Every ball would be in permanent contact with the glass.',
    )
  }
  return half
}

function validateLayers(ctx: Ctx, layers: unknown[], layerIds: Set<string>, dims: Dims): void {
  let planes = 0
  for (let i = 0; i < layers.length; i++) {
    const path = `layers[${i}]`
    const layer = layers[i]
    if (!isPlainObject(layer)) {
      ctx.error(path, 'not-an-object', `expected a layer object, found ${describe(layer)}`)
      continue
    }
    const kind = ctx.oneOf(`${path}.kind`, layer['kind'], ['plane', 'queue', 'ramp', 'wire'])
    if (kind === undefined) continue

    if (kind === 'plane') {
      planes++
      if (ctx.shape(path, layer, ['id', 'kind', 'z'], ['bounds', 'minEntryZ'])) {
        ctx.num(`${path}.z`, layer['z'], -0.5, 0.5)
        if (layer['minEntryZ'] !== undefined) {
          ctx.num(`${path}.minEntryZ`, layer['minEntryZ'], 0, .4)
          if (layer['bounds'] === undefined) ctx.error(path, 'raised-plane-needs-bounds', 'height-qualified decks need explicit bounds')
        }
        // **The field was optional and its *contents* were never looked at** (M9-45): `bounds: 7`
        // and `bounds: { shape: 'triangle' }` both passed, on a field that decides which layer a
        // ball is on and therefore which colliders act on it. The same walk a rollover's trigger
        // volume gets, because it is the same `Region`.
        if (layer['bounds'] !== undefined) validateRegion(ctx, `${path}.bounds`, layer['bounds'], dims)
      }
    } else if (kind === 'queue') {
      if (ctx.shape(path, layer, ['id', 'kind', 'capacity', 'z'], [])) {
        ctx.int(`${path}.capacity`, layer['capacity'], 1, MAX_BALLS)
        ctx.num(`${path}.z`, layer['z'], -0.5, 0.5)
      }
    } else if (kind === 'ramp') {
      if (ctx.shape(path, layer, ['id', 'kind', 'spline', 'width', 'wallHeight', 'entry', 'exit'], [])) {
        validateSpline(ctx, `${path}.spline`, layer['spline'])
        ctx.num(`${path}.width`, layer['width'], 0.02, 0.2)
        ctx.num(`${path}.wallHeight`, layer['wallHeight'], 0.005, 0.1)
        if (ctx.shape(`${path}.entry`, layer['entry'], ['from', 'at'], [])) {
          const e = layer['entry'] as Record<string, unknown>
          checkLayerRef(ctx, `${path}.entry.from`, e['from'], layerIds)
          ctx.point(`${path}.entry.at`, e['at'])
        }
        validateLayerExit(ctx, `${path}.exit`, layer['exit'], layerIds)
      }
    } else {
      if (ctx.shape(path, layer, ['id', 'kind', 'spline', 'captureRadius', 'exit'], ['support'])) {
        validateSpline(ctx, `${path}.spline`, layer['spline'])
        ctx.num(`${path}.captureRadius`, layer['captureRadius'], 0.005, 0.06)
        if(layer['support']!==undefined)ctx.oneOf(`${path}.support`,layer['support'],wireSupportIds)
        validateLayerExit(ctx, `${path}.exit`, layer['exit'], layerIds)
      }
    }
  }
  if (planes === 0) {
    ctx.error('layers', 'no-playfield', 'a machine needs at least one plane layer for the ball to roll on')
  }
}

function validateSpline(ctx: Ctx, path: string, spline: unknown): void {
  // Two control points is a straight run and is legal; the Catmull-Rom sampler duplicates
  // the endpoints. Fewer is not a curve at all.
  const points = ctx.array(path, spline, 2)
  if (!points) return
  for (let i = 0; i < points.length; i++) {
    const p = points[i]
    if (!Array.isArray(p) || p.length !== 3) {
      ctx.error(`${path}[${i}]`, 'not-a-point3', `expected [x, y, z], found ${describe(p)}`)
      continue
    }
    ctx.num(`${path}[${i}][0]`, p[0], -1, 1)
    ctx.num(`${path}[${i}][1]`, p[1], -1, 1)
    ctx.num(`${path}[${i}][2]`, p[2], -0.1, 0.4)
  }
}

function checkLayerRef(ctx: Ctx, path: string, id: unknown, layerIds: Set<string>): void {
  const name = ctx.str(path, id)
  if (name !== undefined && !layerIds.has(name)) {
    ctx.error(path, 'unknown-layer', `no layer is declared with id "${name}"`)
  }
}

function validateLayerExit(ctx: Ctx, path: string, exit: unknown, layerIds: Set<string>): void {
  if (!ctx.shape(path, exit, ['to'], ['at', 'velocity'])) return
  const e = exit as Record<string, unknown>
  checkLayerRef(ctx, `${path}.to`, e['to'], layerIds)
  if (e['at'] !== undefined) ctx.point(`${path}.at`, e['at'])
  if (e['velocity'] !== undefined) ctx.point(`${path}.velocity`, e['velocity'])
}

function validateColliders(
  ctx: Ctx,
  colliders: unknown[],
  layerIds: Set<string>,
  deviceIds: Set<string>,
  dims: Dims,
  outlaneScatter: number | undefined,
  settings: unknown,
): void {
  for (let i = 0; i < colliders.length; i++) {
    const path = `colliders[${i}]`
    const c = colliders[i]
    if (!isPlainObject(c)) {
      ctx.error(path, 'not-an-object', `expected a collider object, found ${describe(c)}`)
      continue
    }
    const kind = ctx.oneOf(`${path}.kind`, c['kind'], COLLIDER_KINDS)
    if (kind === undefined) continue

    const common = ['id', 'layer', 'kind', 'material']
    const commonOptional = ['device', 'outlane', 'adjustedBy',...(kind==='circle'?[]:['thickness'])]
    if(kind!=='circle'&&c['thickness']!==undefined)ctx.num(`${path}.thickness`,c['thickness'],0,.02)
    if (c['adjustedBy'] !== undefined) {
      validateAdjustment(ctx, `${path}.adjustedBy`, c['adjustedBy'], settings)
    }

    if (kind === 'segment') {
      if (!ctx.shape(path, c, [...common, 'a', 'b'], [...commonOptional, 'normal', 'oneSided'])) continue
      const a = ctx.point(`${path}.a`, c['a'])
      const b = ctx.point(`${path}.b`, c['b'])
      if (a && b) {
        if (a[0] === b[0] && a[1] === b[1]) {
          ctx.error(path, 'degenerate-segment', 'a and b are the same point')
        }
        checkInside(ctx, `${path}.a`, a, dims)
        checkInside(ctx, `${path}.b`, b, dims)
      }
      // `normal` is optional even when `oneSided`. 04 §6 first said it was required and in
      // the next paragraph advised omitting it, and the advice is the half that is right:
      // omitted, the solid side is the left-hand perpendicular of a → b, which is correct by
      // construction. Requiring a hand-typed unit vector requires precisely the thing that
      // produces wrong machines — the document's own corrected value, `(0.7150, 0.6991)` to
      // four places, is still out of perpendicular by 7.9e-6, eight times this tolerance.
      if (c['normal'] !== undefined) {
        const n = ctx.point(`${path}.normal`, c['normal'])
        if (n && a && b) checkNormal(ctx, `${path}.normal`, n, a, b)
      }
    } else if (kind === 'arc') {
      if (!ctx.shape(path, c, [...common, 'centre', 'radius', 'angleFrom', 'angleTo', 'insideOut'], commonOptional)) continue
      ctx.point(`${path}.centre`, c['centre'])
      ctx.num(`${path}.radius`, c['radius'], 0.001, 1)
      const from = ctx.num(`${path}.angleFrom`, c['angleFrom'], -10, 10)
      const to = ctx.num(`${path}.angleTo`, c['angleTo'], -10, 10)
      if (from !== undefined && to !== undefined && from === to) {
        ctx.error(path, 'degenerate-arc', 'angleFrom equals angleTo, so the arc has no extent')
      }
      if (typeof c['insideOut'] !== 'boolean') {
        ctx.error(
          `${path}.insideOut`,
          'not-a-boolean',
          'insideOut must be stated explicitly: false contains the ball (a concave corner), ' +
            'true excludes it (a convex obstacle). Getting it backwards is the easiest way ' +
            'to build an unplayable machine, so it has no default.',
        )
      }
    } else if (kind === 'circle') {
      if (!ctx.shape(path, c, [...common, 'centre', 'radius'], commonOptional)) continue
      const centre = ctx.point(`${path}.centre`, c['centre'])
      ctx.num(`${path}.radius`, c['radius'], 0.001, 0.5)
      if (centre) checkInside(ctx, `${path}.centre`, centre, dims)
    } else {
      if (!ctx.shape(path, c, [...common, 'points'], commonOptional)) continue
      const points = ctx.array(`${path}.points`, c['points'], 2)
      if (points) {
        for (let p = 0; p < points.length; p++) {
          const pt = ctx.point(`${path}.points[${p}]`, points[p])
          if (pt) checkInside(ctx, `${path}.points[${p}]`, pt, dims)
        }
      }
    }

    checkLayerRef(ctx, `${path}.layer`, c['layer'], layerIds)
    ctx.oneOf(`${path}.material`, c['material'], MATERIAL_NAMES)
    if (c['outlane'] !== undefined) {
      if (typeof c['outlane'] !== 'boolean') {
        ctx.error(`${path}.outlane`, 'not-a-boolean', 'outlane is a flag: true or false')
      } else if (c['outlane'] === true && outlaneScatter === undefined) {
        // A warning, not an error: the flag is still meaningful documentation of what the
        // surface is, and the machine plays. But it is doing nothing to the physics, and a
        // machine author who marked a surface expects it to.
        ctx.warn(
          `${path}.outlane`,
          'outlane-scatter-unset',
          'this surface is marked as outlane geometry but physics.outlaneScatterDegrees is ' +
            'not set, so it scatters exactly like any other surface of its material',
        )
      }
    }
    if (c['device'] !== undefined) {
      const d = ctx.str(`${path}.device`, c['device'])
      if (d !== undefined && !deviceIds.has(d)) {
        ctx.error(`${path}.device`, 'unknown-device', `no device is declared with id "${d}"`)
      }
    }
  }
}

/**
 * Containment (04 §16). Reported as an **error**: geometry outside the cabinet is not a
 * stylistic choice, it is a ball that leaves the machine.
 */
function checkInside(ctx: Ctx, path: string, p: readonly [number, number], dims: Dims): void {
  const slack = 1e-9
  if (Math.abs(p[0]) > dims.halfWidth + slack || Math.abs(p[1]) > dims.halfLength + slack) {
    ctx.error(
      path,
      'outside-playfield',
      `(${p[0]}, ${p[1]}) is outside the playfield outline ` +
        `(±${dims.halfWidth} × ±${dims.halfLength})`,
    )
  }
}

function validateDevices(
  ctx: Ctx,
  devices: unknown[],
  layerIds: Set<string>,
  deviceIds: Set<string>,
  lampIds: Set<string>,
  switchIds: Set<string>,
  coilIds: Set<string>,
  dims: Dims,
): Set<string> {
  const dropTargets = new Set<string>()

  for (let i = 0; i < devices.length; i++) {
    const path = `devices[${i}]`
    const d = devices[i]
    if (!isPlainObject(d)) {
      ctx.error(path, 'not-an-object', `expected a device object, found ${describe(d)}`)
      continue
    }
    const kind = ctx.oneOf(`${path}.kind`, d['kind'], DEVICE_KINDS)
    if (kind === undefined) continue

    const base = ['id', 'kind']
    const wiring = ['layer', 'coil', 'switch', 'lamp']

    switch (kind) {
      case 'flipper': {
        if (!ctx.shape(path, d, [...base, 'pivot', 'length', 'radiusPivot', 'radiusTip', 'restAngle', 'sweep', 'handedness'],
          [...wiring, 'strength', 'returnTorque', 'coilRampUp', 'eosTorque', 'eosAngleDegrees', 'material'])) break
        const pivot = ctx.point(`${path}.pivot`, d['pivot'])
        if (pivot) checkInside(ctx, `${path}.pivot`, pivot, dims)
        ctx.num(`${path}.length`, d['length'], 0.03, 0.15)
        ctx.num(`${path}.radiusPivot`, d['radiusPivot'], 0.004, 0.03)
        ctx.num(`${path}.radiusTip`, d['radiusTip'], 0.003, 0.03)
        ctx.num(`${path}.restAngle`, d['restAngle'], -7, 7)
        ctx.num(`${path}.sweep`, d['sweep'], 0.2, 1.6)
        ctx.oneOf(`${path}.handedness`, d['handedness'], ['left', 'right'])
        if (d['strength'] !== undefined) ctx.num(`${path}.strength`, d['strength'], 500, 8000)
        // N·m. The floor is a spring that would never return the bat inside PH-A08b's 200 ms;
        // the ceiling is one the coil could not comfortably overpower.
        if (d['returnTorque'] !== undefined) ctx.num(`${path}.returnTorque`, d['returnTorque'], 0.01, 0.4)
        if (d['material'] !== undefined) ctx.oneOf(`${path}.material`, d['material'], MATERIAL_NAMES)
        break
      }
      case 'popBumper': {
        if (!ctx.shape(path, d, [...base, 'centre', 'skirtRadius', 'kickSpeed'], [...wiring, 'cooldownMs'])) break
        const centre = ctx.point(`${path}.centre`, d['centre'])
        if (centre) checkInside(ctx, `${path}.centre`, centre, dims)
        ctx.num(`${path}.skirtRadius`, d['skirtRadius'], 0.01, 0.08)
        ctx.num(`${path}.kickSpeed`, d['kickSpeed'], 0.5, 6)
        if (d['cooldownMs'] !== undefined) ctx.num(`${path}.cooldownMs`, d['cooldownMs'], 0, 2000)
        break
      }
      case 'slingshot': {
        if (!ctx.shape(path, d, [...base, 'a', 'b', 'kickSpeed'], [...wiring, 'triggerSpeed', 'cover'])) break
        checkFace(ctx, path, d, dims)
        ctx.num(`${path}.kickSpeed`, d['kickSpeed'], 0.5, 7)
        if(d['cover']!==undefined)ctx.oneOf(`${path}.cover`,d['cover'],slingCoverIds)
        // **Bounded, and it was not.** `triggerSpeed` was listed as a member and never checked, so a
        // machine could ask for a slingshot that fires on a ball travelling at −4 m/s — which is
        // every ball, in both directions — or at 900, which is none. Found by M9-03's inspector
        // asking the validator what each of its sliders may be set to. The band is the speed a ball
        // can arrive at: below the floor a resting ball trips it, above the ceiling nothing does.
        if (d['triggerSpeed'] !== undefined) ctx.num(`${path}.triggerSpeed`, d['triggerSpeed'], 0.1, 7)
        break
      }
      case 'standupTarget':
      case 'dropTarget': {
        if (!ctx.shape(path, d, [...base, 'a', 'b'], [...wiring, 'dropSpeed', 'solidBack'])) break
        checkFace(ctx, path, d, dims)
        if (d['solidBack'] !== undefined) ctx.bool(`${path}.solidBack`, d['solidBack'])
        if (d['dropSpeed'] !== undefined) ctx.num(`${path}.dropSpeed`, d['dropSpeed'], 0.1, 5)
        if (kind === 'dropTarget' && typeof d['id'] === 'string') dropTargets.add(d['id'])
        break
      }
      case 'dropTargetBank': {
        if (!ctx.shape(path, d, [...base, 'targets'], [...wiring, 'autoResetMs'])) break
        ctx.array(`${path}.targets`, d['targets'], 1)
        if (d['autoResetMs'] !== undefined) ctx.num(`${path}.autoResetMs`, d['autoResetMs'], 0, 60000)
        break
      }
      case 'spinner': {
        if (!ctx.shape(path, d, [...base, 'a', 'b', 'vaneMass', 'shaftHeight', 'vaneDrop', 'damping'], wiring)) break
        checkFace(ctx, path, d, dims)
        // 04 §7: the vane is described the way it can be weighed. A real one is about 4 g.
        ctx.num(`${path}.vaneMass`, d['vaneMass'], 0.0005, 0.05)
        ctx.num(`${path}.shaftHeight`, d['shaftHeight'], 0.015, 0.08)
        ctx.num(`${path}.vaneDrop`, d['vaneDrop'], 0.005, 0.07)
        ctx.num(`${path}.damping`, d['damping'], 0, 20)
        break
      }
      case 'kicker':
      case 'plunger':
      case 'autoPlunger': {
        if (!ctx.shape(path, d, [...base, 'centre', 'captureRadius', 'holdMs', 'exit'], [...wiring, 'captureSpeed', 'rearmMs', 'lockable'])) break
        if (d['lockable'] !== undefined && typeof d['lockable'] !== 'boolean') ctx.error(`${path}.lockable`, 'not-a-boolean', 'lockable must be boolean')
        const centre = ctx.point(`${path}.centre`, d['centre'])
        if (centre) checkInside(ctx, `${path}.centre`, centre, dims)
        ctx.num(`${path}.captureRadius`, d['captureRadius'], 0.008, 0.06)
        ctx.num(`${path}.holdMs`, d['holdMs'], -1, 120000)
        if (ctx.shape(`${path}.exit`, d['exit'], ['direction', 'speed'], ['to'])) {
          const e = d['exit'] as Record<string, unknown>
          if (e['to'] !== undefined) checkLayerRef(ctx, `${path}.exit.to`, e['to'], layerIds)
          const dir = ctx.point(`${path}.exit.direction`, e['direction'])
          if (dir && dir[0] === 0 && dir[1] === 0) {
            ctx.error(`${path}.exit.direction`, 'zero-vector', 'exit direction has no direction')
          }
          ctx.num(`${path}.exit.speed`, e['speed'], 0.1, 9)
        }
        // Both were members nothing checked — see the note on `triggerSpeed`. A capture speed is a
        // speed and takes the same band as the exit it belongs to; a re-arm delay is a delay and
        // takes `autoResetMs`'s minute.
        if (d['captureSpeed'] !== undefined) ctx.num(`${path}.captureSpeed`, d['captureSpeed'], 0.1, 9)
        if (d['rearmMs'] !== undefined) ctx.num(`${path}.rearmMs`, d['rearmMs'], 0, 60000)
        break
      }
      case 'magnet': {
        if (!ctx.shape(path, d, [...base, 'centre', 'radius', 'peakForce', 'falloff'], [...wiring,'releaseAfterMs'])) break
        const centre = ctx.point(`${path}.centre`, d['centre'])
        if (centre) checkInside(ctx, `${path}.centre`, centre, dims)
        ctx.num(`${path}.radius`, d['radius'], 0.01, 0.2)
        ctx.num(`${path}.peakForce`, d['peakForce'], 0.01, 5.25)
        if(d['releaseAfterMs']!==undefined)ctx.num(`${path}.releaseAfterMs`,d['releaseAfterMs'],20,500)
        ctx.oneOf(`${path}.falloff`, d['falloff'], ['inverseSquare', 'linear'])
        break
      }
      case 'gate':
      case 'ballSaveGate':
      case 'diverter': {
        if (!ctx.shape(path, d, [...base, 'a', 'b', 'passDirection'], [...wiring, 'armMass','mountContacts'])) break
        const a = ctx.point(`${path}.a`, d['a'])
        const b = ctx.point(`${path}.b`, d['b'])
        const pass = ctx.point(`${path}.passDirection`, d['passDirection'])
        if(d['mountContacts']!==undefined)ctx.bool(`${path}.mountContacts`,d['mountContacts'])
        if (a) checkInside(ctx, `${path}.a`, a, dims)
        if (b) checkInside(ctx, `${path}.b`, b, dims)
        if (a && b && pass) checkNormal(ctx, `${path}.passDirection`, pass, a, b)
        if (d['armMass'] !== undefined) ctx.num(`${path}.armMass`, d['armMass'], 0.0005, 0.05)
        break
      }
      case 'rollover': {
        if (!ctx.shape(path, d, [...base, 'region'], [...wiring, 'debounceMs', 'zRange'])) break
        validateRegion(ctx, `${path}.region`, d['region'], dims)
        if(d['zRange']!==undefined){const z=ctx.point(`${path}.zRange`,d['zRange']);if(z){ctx.num(`${path}.zRange[0]`,z[0],0,.5);ctx.num(`${path}.zRange[1]`,z[1],0,.5);if(z[0]>=z[1])ctx.error(`${path}.zRange`,'not-a-range','range must be [min, max] with min < max')}}
        if (d['debounceMs'] !== undefined) ctx.num(`${path}.debounceMs`, d['debounceMs'], 0, 10000)
        break
      }
      case 'captiveBall': {
        if (!ctx.shape(path, d, [...base, 'centre', 'travel', 'direction'], [...wiring,'cageContacts'])) break
        if(d['cageContacts']!==undefined)ctx.bool(`${path}.cageContacts`,d['cageContacts'])
        const centre = ctx.point(`${path}.centre`, d['centre'])
        if (centre) checkInside(ctx, `${path}.centre`, centre, dims)
        ctx.num(`${path}.travel`, d['travel'], 0.005, 0.2)
        ctx.point(`${path}.direction`, d['direction'])
        break
      }
    }

    if (d['layer'] !== undefined) checkLayerRef(ctx, `${path}.layer`, d['layer'], layerIds)
    checkWiring(ctx, `${path}.coil`, d['coil'], coilIds, 'coil')
    checkWiring(ctx, `${path}.switch`, d['switch'], switchIds, 'switch')
    checkWiring(ctx, `${path}.lamp`, d['lamp'], lampIds, 'lamp')
  }

  // Silences the unused-parameter warning while keeping the signature honest about what a
  // fuller reachability pass (M2-21) will need.
  void deviceIds
  return dropTargets
}

function checkFace(ctx: Ctx, path: string, d: Record<string, unknown>, dims: Dims): void {
  const a = ctx.point(`${path}.a`, d['a'])
  const b = ctx.point(`${path}.b`, d['b'])
  if (!a || !b) return
  if (a[0] === b[0] && a[1] === b[1]) {
    ctx.error(path, 'degenerate-face', 'a and b are the same point')
  }
  checkInside(ctx, `${path}.a`, a, dims)
  checkInside(ctx, `${path}.b`, b, dims)
}

function checkWiring(ctx: Ctx, path: string, id: unknown, known: Set<string>, what: string): void {
  if (id === undefined) return
  const name = ctx.str(path, id)
  if (name !== undefined && !known.has(name)) {
    ctx.error(path, `unknown-${what}`, `no ${what} is declared with id "${name}"`)
  }
}

/**
 * A collider the operator menu moves (04 §6, 04 §15, M9-36).
 *
 * **Both refusals below exist because the failure is otherwise silent and looks like a working
 * machine.** An adjustment naming a setting the machine does not declare moves nothing; one
 * missing an option moves nothing when a player picks that option — and `settingsHash` folds the
 * chosen value into the leaderboard key either way, so two players on different settings rank
 * separately on a machine that plays identically for both.
 *
 * `settings` arrives as the raw block rather than as a resolved map because that is what says
 * which *options* a setting declares; a resolved map only carries the default.
 */
function validateAdjustment(ctx: Ctx, path: string, adjust: unknown, settings: unknown): void {
  if (!ctx.shape(path, adjust, ['setting', 'axis', 'offsets'], [])) return
  const a = adjust as Record<string, unknown>
  ctx.oneOf(`${path}.axis`, a['axis'], ['x', 'y'])

  const name = ctx.str(`${path}.setting`, a['setting'])
  const block = isPlainObject(settings) ? settings : undefined
  const spec = name !== undefined && block !== undefined ? block[name] : undefined
  if (name !== undefined && !isPlainObject(spec)) {
    ctx.error(
      `${path}.setting`,
      'unknown-setting',
      `no operator setting is declared with id "${name}", so nothing would ever move this collider`,
    )
  }

  if (!isPlainObject(a['offsets'])) {
    ctx.error(`${path}.offsets`, 'not-an-object', `expected an offsets object, found ${describe(a['offsets'])}`)
    return
  }
  const offsets = a['offsets']
  const given = Object.keys(offsets).sort()
  for (const option of given) {
    // Metres, and bounded far tighter than the playfield: this moves a post on its own bolt, and
    // an adjustment that can relocate a collider across the machine is a different feature.
    ctx.num(`${path}.offsets.${option}`, offsets[option], -0.05, 0.05)
  }
  if (!isPlainObject(spec)) return

  const declared = Array.isArray(spec['options'])
    ? (spec['options'] as unknown[]).filter((o): o is string => typeof o === 'string')
    : undefined
  if (declared === undefined) {
    ctx.error(
      `${path}.setting`,
      'adjustment-incomplete',
      `"${name!}" is not a choice — it declares no options — so there is nothing for these ` +
        `offsets to be keyed on`,
    )
    return
  }
  for (const option of declared) {
    if (!(option in offsets)) {
      ctx.error(
        `${path}.offsets`,
        'adjustment-incomplete',
        `"${name!}" offers the option "${option}" and this adjustment has no offset for it, so ` +
          `choosing it would move nothing while still changing the leaderboard key`,
      )
    }
  }
  for (const option of given) {
    if (!declared.includes(option)) {
      ctx.error(
        `${path}.offsets.${option}`,
        'adjustment-incomplete',
        `"${name!}" declares no option "${option}", so this offset can never be chosen`,
      )
    }
  }
}

function validateRegion(ctx: Ctx, path: string, region: unknown, dims: Dims): void {
  if (!isPlainObject(region)) {
    ctx.error(path, 'not-an-object', `expected a region, found ${describe(region)}`)
    return
  }
  const shape = ctx.oneOf(`${path}.shape`, region['shape'], ['circle', 'rect'])
  if (shape === 'circle') {
    if (!ctx.shape(path, region, ['shape', 'centre', 'radius'], [])) return
    const centre = ctx.point(`${path}.centre`, region['centre'])
    ctx.num(`${path}.radius`, region['radius'], 0.002, 0.3)
    if (centre) checkInside(ctx, `${path}.centre`, centre, dims)
  } else if (shape === 'rect') {
    if (!ctx.shape(path, region, ['shape', 'min', 'max'], [])) return
    const min = ctx.point(`${path}.min`, region['min'])
    const max = ctx.point(`${path}.max`, region['max'])
    if (min && max && (min[0] >= max[0] || min[1] >= max[1])) {
      ctx.error(path, 'inverted-rect', 'min must be strictly below and left of max')
    }
  }
}

function validateDropTargetBanks(ctx: Ctx, devices: unknown[], dropTargets: Set<string>): void {
  for (let i = 0; i < devices.length; i++) {
    const d = devices[i]
    if (!isPlainObject(d) || d['kind'] !== 'dropTargetBank') continue
    const targets = d['targets']
    if (!Array.isArray(targets)) continue
    for (let t = 0; t < targets.length; t++) {
      const id = targets[t]
      if (typeof id !== 'string' || !dropTargets.has(id)) {
        ctx.error(
          `devices[${i}].targets[${t}]`,
          'unknown-drop-target',
          `${describe(id)} does not name a declared dropTarget device`,
        )
      }
    }
  }
}

/**
 * Named playfield areas.
 *
 * Returns the ids so a lamp naming one can be checked against them. A machine with no regions
 * is fine — most machines have none — but a machine that *uses* one has to have declared it,
 * which is the whole reason this exists.
 */
function validateRegions(ctx: Ctx, regions: unknown, dims: Dims): Set<string> {
  const ids = new Set<string>()
  if (regions === undefined) return ids
  const list = ctx.array('regions', regions, 0)
  if (!list) return ids

  for (let i = 0; i < list.length; i++) {
    const path = `regions[${i}]`
    const r = list[i]
    if (!isPlainObject(r)) {
      ctx.error(path, 'not-an-object', `expected a region object, found ${describe(r)}`)
      continue
    }
    if (!ctx.shape(path, r, ['id', 'min', 'max'], [])) continue
    const id = ctx.str(`${path}.id`, r['id'])
    if (id !== undefined) {
      if (ids.has(id)) ctx.error(`${path}.id`, 'duplicate-id', `region "${id}" is declared twice`)
      ids.add(id)
    }
    const min = ctx.point(`${path}.min`, r['min'])
    const max = ctx.point(`${path}.max`, r['max'])
    if (min === undefined || max === undefined) continue
    if (max[0] <= min[0] || max[1] <= min[1]) {
      ctx.error(`${path}`, 'inside-out-region', 'max must be above and to the right of min')
    }
    if (
      min[0] < -dims.halfWidth ||
      max[0] > dims.halfWidth ||
      min[1] < -dims.halfLength ||
      max[1] > dims.halfLength
    ) {
      ctx.error(path, 'region-off-playfield', 'a region must lie within the playfield')
    }
  }
  return ids
}

function validateLamps(ctx: Ctx, lamps: unknown[], regions: Set<string>): Set<string> {
  const groups = new Set<string>()
  for (let i = 0; i < lamps.length; i++) {
    const path = `lamps[${i}]`
    const l = lamps[i]
    if (!isPlainObject(l)) {
      ctx.error(path, 'not-an-object', `expected a lamp object, found ${describe(l)}`)
      continue
    }
    if (!ctx.shape(path, l, ['id', 'kind', 'colour'],
      ['shape', 'position', 'rotation', 'size', 'radius', 'region', 'group'])) continue
    const kind = ctx.oneOf(`${path}.kind`, l['kind'], ['insert', 'gi', 'flasher'])
    const colour = ctx.str(`${path}.colour`, l['colour'])
    if (colour !== undefined && !/^#[0-9A-Fa-f]{6}$/.test(colour)) {
      ctx.error(`${path}.colour`, 'not-a-colour', `"${colour}" is not a #RRGGBB hex colour`)
    }
    if (kind === 'insert') {
      if (l['position'] === undefined) {
        ctx.error(`${path}.position`, 'missing-field', 'an insert needs a position on the playfield')
      } else {
        ctx.point(`${path}.position`, l['position'])
      }
    }
    if (kind === 'gi' && l['region'] === undefined) {
      ctx.error(`${path}.region`, 'missing-field', 'general illumination needs a named region')
    }
    // And the name has to mean something. Until the format could declare regions, this field
    // was required and unresolvable: any string passed, and nothing could light anything.
    const region = l['region']
    if (typeof region === 'string' && !regions.has(region)) {
      ctx.error(
        `${path}.region`,
        'unknown-region',
        `no region named "${region}" is declared; add it to the machine's "regions"`,
      )
    }
    if (typeof l['group'] === 'string') groups.add(l['group'])
  }
  return groups
}

/**
 * Playfield toys (04, 05 §3).
 *
 * A toy is a **state display**, so almost everything here is a reference check: a dome whose
 * charge variable no rulesheet declares is wired to nothing, and it looks exactly like a working
 * one — lit, pulsing, permanently on its first appearance — until somebody plays the machine and
 * notices it never changes.
 *
 * The one rule that is not a reference check is the pulse rate, and it is a **safety** rule.
 * NFR-026: nothing in this product flashes between 3 Hz and 55 Hz at high contrast. The toy is
 * the brightest thing on the playfield and the thing a player is asked to look at, so a machine
 * that declares a faster pulse is refused rather than quietly slowed down — a silently corrected
 * value is one the author will type again, and a player-authored machine (M6) is exactly where
 * this would otherwise arrive.
 */
function validateToys(
  ctx: Ctx,
  toys: unknown,
  layerIds: Set<string>,
  geometryIds: Map<string, string>,
  rules: RuleNames,
): void {
  const list = ctx.array('toys', toys, 0)
  if (list === undefined) return
  for (let i = 0; i < list.length; i++) {
    const path = `toys[${i}]`
    const t = list[i]
    if (!isPlainObject(t)) {
      ctx.error(path, 'not-an-object', `expected a toy object, found ${describe(t)}`)
      continue
    }
    if (
      !ctx.shape(
        path,
        t,
        ['id', 'kind', 'layer', 'centre', 'radius', 'height', 'chargeVar', 'stages'],
        ['collapse', 'climax'],
      )
    ) {
      continue
    }
    const id = ctx.str(`${path}.id`, t['id'])
    if (id !== undefined) {
      // The same namespace as colliders and devices: a toy sits among them on the playfield,
      // and two things on the wood answering to one name is how a reference becomes ambiguous.
      const previous = geometryIds.get(id)
      if (previous !== undefined) {
        ctx.error(`${path}.id`, 'duplicate-id', `id "${id}" is already used by ${previous}`)
      } else {
        geometryIds.set(id, path)
      }
    }
    ctx.oneOf(`${path}.kind`, t['kind'], ['dome','hull','portal'])
    const layer = ctx.str(`${path}.layer`, t['layer'])
    if (layer !== undefined && !layerIds.has(layer)) {
      ctx.error(`${path}.layer`, 'unknown-layer', `no layer named "${layer}" is declared`)
    }
    ctx.point(`${path}.centre`, t['centre'])
    ctx.num(`${path}.radius`, t['radius'], 0.005, 0.5)
    ctx.num(`${path}.height`, t['height'], 0.005, 0.5)

    const chargeVar = ctx.str(`${path}.chargeVar`, t['chargeVar'])
    if (chargeVar !== undefined && !rules.varIds.has(chargeVar)) {
      ctx.error(
        `${path}.chargeVar`,
        'unknown-var',
        `no rule variable named "${chargeVar}" is declared; the toy would never change`,
      )
    }

    validateToyStages(ctx, `${path}.stages`, t['stages'])

    const collapse = t['collapse']
    if (collapse !== undefined) {
      if (ctx.shape(`${path}.collapse`, collapse, ['var', 'stages', 'shrinkTo'], [])) {
        const c = collapse as Record<string, unknown>
        const name = ctx.str(`${path}.collapse.var`, c['var'])
        if (name !== undefined && !rules.varIds.has(name)) {
          ctx.error(`${path}.collapse.var`, 'unknown-var', `no rule variable named "${name}" is declared`)
        }
        ctx.num(`${path}.collapse.stages`, c['stages'], 1, 99)
        ctx.num(`${path}.collapse.shrinkTo`, c['shrinkTo'], 0.05, 1)
      }
    }

    const climax = t['climax']
    if (climax !== undefined) {
      if (ctx.shape(`${path}.climax`, climax, ['mode', 'holdMs'], [])) {
        const c = climax as Record<string, unknown>
        const mode = ctx.str(`${path}.climax.mode`, c['mode'])
        if (mode !== undefined && !rules.modeIds.has(mode)) {
          ctx.error(`${path}.climax.mode`, 'unknown-mode', `no mode named "${mode}" is declared`)
        }
        ctx.num(`${path}.climax.holdMs`, c['holdMs'], 50, 5000)
      }
    }
  }
}

/** The safe pulse ceiling, in hertz. Below NFR-026's 3 Hz floor, not at it. */
export const MAX_TOY_PULSE_HZ = 2.9

function validateToyStages(ctx: Ctx, path: string, stages: unknown): void {
  const list = ctx.array(path, stages, 1)
  if (list === undefined) return
  let previousFrom = Number.NEGATIVE_INFINITY
  for (let i = 0; i < list.length; i++) {
    const at = `${path}[${i}]`
    const stage = list[i]
    if (!isPlainObject(stage)) {
      ctx.error(at, 'not-an-object', `expected a stage object, found ${describe(stage)}`)
      continue
    }
    if (!ctx.shape(at, stage, ['from', 'colour', 'pulseHz'], ['coronaRings', 'flicker', 'collapsing'])) {
      continue
    }
    const from = ctx.num(`${at}.from`, stage['from'], 0, 1e6)
    if (from !== undefined) {
      // Ascending, because the renderer picks the last stage whose `from` the value has passed.
      // Declared out of order, a machine would display a stage the author never chose.
      if (from <= previousFrom) {
        ctx.error(
          `${at}.from`,
          'stages-out-of-order',
          `stages must ascend; ${from} follows ${previousFrom}`,
        )
      }
      previousFrom = from
    }
    const colour = ctx.str(`${at}.colour`, stage['colour'])
    if (colour !== undefined && !/^#[0-9A-Fa-f]{6}$/.test(colour)) {
      ctx.error(`${at}.colour`, 'not-a-colour', `"${colour}" is not a #RRGGBB hex colour`)
    }
    const hz = ctx.num(`${at}.pulseHz`, stage['pulseHz'], 0, 60)
    if (hz !== undefined && hz > MAX_TOY_PULSE_HZ) {
      ctx.error(
        `${at}.pulseHz`,
        'flashes-in-the-seizure-band',
        `${hz} Hz is inside NFR-026's 3–55 Hz band; the ceiling is ${MAX_TOY_PULSE_HZ} Hz`,
      )
    }
    if (stage['coronaRings'] !== undefined) ctx.num(`${at}.coronaRings`, stage['coronaRings'], 0, 3)
    if (stage['flicker'] !== undefined) ctx.num(`${at}.flicker`, stage['flicker'], 0, 1)
    if (stage['collapsing'] !== undefined && typeof stage['collapsing'] !== 'boolean') {
      ctx.error(`${at}.collapsing`, 'not-a-boolean', `expected true or false, found ${describe(stage['collapsing'])}`)
    }
  }
}

function validateSwitches(ctx: Ctx, switches: unknown[], deviceIds: Set<string>): void {
  for (let i = 0; i < switches.length; i++) {
    const path = `switches[${i}]`
    const s = switches[i]
    if (!isPlainObject(s)) {
      ctx.error(path, 'not-an-object', `expected a switch object, found ${describe(s)}`)
      continue
    }
    if (!ctx.shape(path, s, ['id'], ['kind', 'device', 'debounceMs', 'perRevolution', 'index'])) continue
    const kind = s['kind'] === undefined
      ? 'device'
      : ctx.oneOf(`${path}.kind`, s['kind'], ['device', 'tilt', 'slamTilt', 'trough', 'virtual'])
    if (kind === 'device') {
      const dev = ctx.str(`${path}.device`, s['device'])
      if (dev !== undefined && !deviceIds.has(dev)) {
        ctx.error(`${path}.device`, 'unknown-device', `no device is declared with id "${dev}"`)
      }
    }
    if (s['debounceMs'] !== undefined) ctx.num(`${path}.debounceMs`, s['debounceMs'], 0, 5000)
    if (s['index'] !== undefined) ctx.int(`${path}.index`, s['index'], 0, MAX_BALLS)
  }
}

function validateCoils(ctx: Ctx, coils: unknown[]): void {
  for (let i = 0; i < coils.length; i++) {
    const path = `coils[${i}]`
    const c = coils[i]
    if (!isPlainObject(c)) {
      ctx.error(path, 'not-an-object', `expected a coil object, found ${describe(c)}`)
      continue
    }
    if (!ctx.shape(path, c, ['id', 'kind'], ['pulseMs'])) continue
    ctx.oneOf(`${path}.kind`, c['kind'], ['flipper', 'kicker', 'bumper', 'knocker', 'diverter', 'magnet', 'virtual'])
    if (c['pulseMs'] !== undefined) ctx.num(`${path}.pulseMs`, c['pulseMs'], 1, 5000)
  }
}

/**
 * Total balls the machine's queue layers can hold.
 *
 * Zero when the machine declares none, which is not an error — a test rig or a Builder machine
 * in progress may have no trough at all, and refusing to validate it would make the format
 * harder to work in for no safety gained. The rule only fires when there *is* a trough and it
 * is too small for the balls the machine asks for.
 */
function troughCapacityOf(m: Record<string, unknown>): number {
  const layers = m['layers']
  if (!Array.isArray(layers)) return 0
  let total = 0
  for (const layer of layers) {
    if (!isPlainObject(layer) || layer['kind'] !== 'queue') continue
    const capacity = layer['capacity']
    if (typeof capacity === 'number' && Number.isFinite(capacity)) total += capacity
  }
  return total
}

function validatePhysics(ctx: Ctx, physics: unknown, troughCapacity: number): void {
  if (!ctx.shape('physics', physics, [], ['materials', 'ballCount', 'ballSpeedClamp', 'outlaneScatterDegrees','persistentBallSearch','flipperCradleProtection'])) return
  const p = physics as Record<string, unknown>
  if (p['ballCount'] !== undefined) {
    ctx.int('physics.ballCount', p['ballCount'], 1, MAX_BALLS)
    // Against the machine's own trough, not just the engine's ceiling.
    //
    // A queue layer states a `capacity`, and until this rule nothing anywhere read it: a
    // machine could declare a three-ball trough and six balls and load perfectly, which is a
    // physically impossible machine that behaves like a working one right up to the moment a
    // multiball tries to serve a ball from a trough that cannot hold it.
    const count = p['ballCount']
    if (typeof count === 'number' && troughCapacity > 0 && count > troughCapacity) {
      ctx.error(
        'physics.ballCount',
        'balls-exceed-trough',
        `${count} balls, but the machine's queue layers hold ${troughCapacity}`,
      )
    }
  }
  if(p['persistentBallSearch']!==undefined)ctx.bool('physics.persistentBallSearch',p['persistentBallSearch'])
  if(p['flipperCradleProtection']!==undefined)ctx.bool('physics.flipperCradleProtection',p['flipperCradleProtection'])
  if (p['ballSpeedClamp'] !== undefined) ctx.num('physics.ballSpeedClamp', p['ballSpeedClamp'], 4, 10)
  if (p['outlaneScatterDegrees'] !== undefined) {
    ctx.num('physics.outlaneScatterDegrees', p['outlaneScatterDegrees'], 0, SCATTER_MAX_DEGREES)
  }
  if (p['materials'] === undefined) return
  if (!isPlainObject(p['materials'])) {
    ctx.error('physics.materials', 'not-an-object', 'expected a map of material overrides')
    return
  }
  const overrides = p['materials']
  for (const name of Object.keys(overrides).sort()) {
    const path = `physics.materials.${name}`
    if (!(MATERIAL_NAMES as readonly string[]).includes(name)) {
      ctx.error(path, 'unknown-material', `"${name}" is not a material. Known: ${MATERIAL_NAMES.join(', ')}`)
      continue
    }
    if (!ctx.shape(path, overrides[name], [], ['friction', 'restitution', 'falloff', 'scatterDegrees'])) continue
    const o = overrides[name] as Record<string, unknown>
    for (const key of ['friction', 'restitution', 'falloff', 'scatterDegrees'] as const) {
      if (o[key] === undefined) continue
      const [lo, hi] = MATERIAL_OVERRIDE_BOUNDS[key]
      ctx.num(`${path}.${key}`, o[key], lo, hi)
    }
  }
}

/**
 * Warn about every lamp nothing can ever light (M9-40, 04 §16).
 *
 * See the call site for why this exists and why it is a warning. The three ways a lamp is reachable
 * are checked in the order they are cheap: its own id, its group, then the devices.
 */
function checkLampsAreLit(
  ctx: Ctx,
  lamps: unknown[],
  devices: unknown[],
  names: { readonly lampsLit: Set<string>; readonly groupsLit: Set<string> },
): void {
  // A device that carries a lamp lights it with no rule anywhere: a pop bumper's ring, a
  // rollover's arrow. Collected once rather than searched per lamp.
  const onDevices = new Set<string>()
  for (const device of devices) {
    if (!isPlainObject(device)) continue
    if (typeof device['lamp'] === 'string') onDevices.add(device['lamp'])
  }

  for (let i = 0; i < lamps.length; i++) {
    const lamp = lamps[i]
    if (!isPlainObject(lamp)) continue
    const id = lamp['id']
    if (typeof id !== 'string') continue
    if (names.lampsLit.has(id) || onDevices.has(id)) continue
    const group = lamp['group']
    if (typeof group === 'string' && names.groupsLit.has(group)) continue
    ctx.warn(
      `lamps[${i}]`,
      'lamp-never-lit',
      `nothing addresses lamp "${id}" — no rule names it, no rule names its group, and no ` +
        'device carries it, so it is dark for the life of the machine',
    )
  }
}

/** The identifier namespaces a rulesheet declares, for anything that has to name one. */
interface RuleNames {
  readonly varIds: Set<string>
  readonly modeIds: Set<string>
  /** Every lamp and lamp group the rulesheet addresses (M9-40). See `ActionScope.lampsLit`. */
  readonly lampsLit: Set<string>
  readonly groupsLit: Set<string>
}

interface RuleRefs {
  deviceIds: Set<string>
  lampIds: Set<string>
  switchIds: Set<string>
  coilIds: Set<string>
  lampGroups: Set<string>
  sceneIds: Set<string>
  cueIds: Set<string>
  speechIds: Set<string>
  /** A machine with no `display` block at all cannot have dangling scene references. */
  hasDisplay: boolean
  hasAudio: boolean
  /**
   * The machine declares 04 §15's `ballSaveSeconds` (M9-35).
   *
   * Read from the settings block's *keys* rather than from `validateSettings`, which runs after the
   * rules — deliberately, because a rule naming a scene or a cue is checked against sets the
   * presentation passes produce. A key is enough: whether the value is well formed is that pass's
   * question, and a rule deferring to a malformed setting has two errors, not one.
   */
  hasOperatorBallSave: boolean
}

/**
 * Validate the rulesheet, and hand back the names it declares.
 *
 * The sets come back out because the *toys* are checked against them: a dome that displays a
 * variable no rulesheet declares is a state display wired to nothing, which looks exactly like
 * a working one until the machine is played.
 */
function validateRules(ctx: Ctx, rules: unknown, refs: RuleRefs): RuleNames {
  if (!ctx.shape('rules', rules, [], ['vars', 'timers', 'modes', 'rules', 'scoring'])) {
    return { varIds: new Set(), modeIds: new Set(), lampsLit: new Set(), groupsLit: new Set() }
  }
  const r = rules as Record<string, unknown>

  const varIds = new Set<string>()
  const varList = Array.isArray(r['vars']) ? r['vars'] : []
  for (let i = 0; i < varList.length; i++) {
    const path = `rules.vars[${i}]`
    const v = varList[i]
    if (!ctx.shape(path, v, ['id', 'scope', 'type'], ['initial', 'max', 'members'])) continue
    const vv = v as Record<string, unknown>
    const id = ctx.str(`${path}.id`, vv['id'])
    if (id !== undefined) {
      if (varIds.has(id)) ctx.error(`${path}.id`, 'duplicate-id', `variable "${id}" is declared twice`)
      varIds.add(id)
    }
    ctx.oneOf(`${path}.scope`, vv['scope'], ['game', 'player', 'ball'])
    const type = ctx.oneOf(`${path}.type`, vv['type'], ['int', 'bool', 'set'])
    if (type === 'set') {
      const members = ctx.array(`${path}.members`, vv['members'], 1)
      if (members) {
        for (let mi = 0; mi < members.length; mi++) ctx.str(`${path}.members[${mi}]`, members[mi])
      }
    }
    if (vv['max'] !== undefined) ctx.num(`${path}.max`, vv['max'], 0, 1e12)
  }

  const timerIds = new Set<string>()
  const timerList = Array.isArray(r['timers']) ? r['timers'] : []
  for (let i = 0; i < timerList.length; i++) {
    const path = `rules.timers[${i}]`
    if (!ctx.shape(path, timerList[i], ['id', 'durationMs'], ['pausesDuringMultiball'])) continue
    const t = timerList[i] as Record<string, unknown>
    const id = ctx.str(`${path}.id`, t['id'])
    if (id !== undefined) {
      if (timerIds.has(id)) ctx.error(`${path}.id`, 'duplicate-id', `timer "${id}" is declared twice`)
      timerIds.add(id)
    }
    ctx.num(`${path}.durationMs`, t['durationMs'], 1, 3600000)
  }

  // Mode ids are collected before any action is walked, so a rule declared above the mode it
  // starts still resolves. A rulesheet is not a program and has no forward-declaration rule.
  const modeIds = new Set<string>()
  const modeList = Array.isArray(r['modes']) ? r['modes'] : []
  for (let i = 0; i < modeList.length; i++) {
    const mode = modeList[i]
    if (!isPlainObject(mode)) continue
    const id = mode['id']
    if (typeof id === 'string') modeIds.add(id)
  }

  const scope: ActionScope = {
    refs,
    varIds,
    timerIds,
    modeIds,
    lampGroups: refs.lampGroups,
    started: new Set<string>(),
    ended: new Set<string>(),
    lampsLit: new Set<string>(),
    groupsLit: new Set<string>(),
  }

  const seenModes = new Set<string>()
  for (let i = 0; i < modeList.length; i++) {
    const path = `rules.modes[${i}]`
    if (!ctx.shape(path, modeList[i], ['id', 'title'],
      ['timer', 'shots', 'onStart', 'onShot', 'onComplete', 'onTimeout', 'stackable'])) continue
    const mode = modeList[i] as Record<string, unknown>
    const id = ctx.str(`${path}.id`, mode['id'])
    if (id !== undefined) {
      if (seenModes.has(id)) ctx.error(`${path}.id`, 'duplicate-id', `mode "${id}" is declared twice`)
      seenModes.add(id)
    }
    ctx.str(`${path}.title`, mode['title'])
    if (mode['timer'] !== undefined) {
      const t = ctx.str(`${path}.timer`, mode['timer'])
      if (t !== undefined && !timerIds.has(t)) {
        ctx.error(`${path}.timer`, 'unknown-timer', `no timer is declared with id "${t}"`)
      }
    }
    if (mode['shots'] !== undefined) {
      const shots = ctx.array(`${path}.shots`, mode['shots'], 1)
      if (shots) {
        for (let s = 0; s < shots.length; s++) {
          const shot = ctx.str(`${path}.shots[${s}]`, shots[s])
          if (shot !== undefined && !refs.switchIds.has(shot)) {
            ctx.error(`${path}.shots[${s}]`, 'unknown-switch', `no switch is declared with id "${shot}"`)
          }
        }
      }
    }
    for (const hook of ['onStart', 'onShot', 'onComplete', 'onTimeout']) {
      if (mode[hook] !== undefined) checkActions(ctx, `${path}.${hook}`, mode[hook], scope)
    }
  }

  const ruleList = Array.isArray(r['rules']) ? r['rules'] : []
  const ruleIds = new Set<string>()
  for (let i = 0; i < ruleList.length; i++) {
    const path = `rules.rules[${i}]`
    if (!ctx.shape(path, ruleList[i], ['id', 'on', 'do'], ['when'])) continue
    const rule = ruleList[i] as Record<string, unknown>
    const id = ctx.str(`${path}.id`, rule['id'])
    if (id !== undefined) {
      if (ruleIds.has(id)) ctx.error(`${path}.id`, 'duplicate-id', `rule "${id}" is declared twice`)
      ruleIds.add(id)
    }
    const on = ctx.str(`${path}.on`, rule['on'])
    if (on !== undefined) checkTrigger(ctx, `${path}.on`, on, refs, varIds, timerIds, modeIds)
    if (rule['when'] !== undefined) {
      checkExpression(ctx, `${path}.when`, rule['when'], 1, { nodes: 0 })
    }
    checkActions(ctx, `${path}.do`, rule['do'], scope)
  }

  // 04 §16 "Rules — reachability". An **error**, not a warning: a mode nothing can start is a
  // feature the player paid for and cannot see, and a mode nothing can end is a machine that
  // stops responding until the ball drains.
  for (let i = 0; i < modeList.length; i++) {
    const mode = modeList[i]
    if (!isPlainObject(mode) || typeof mode['id'] !== 'string') continue
    const id = mode['id']
    const path = `rules.modes[${i}]`
    if (!scope.started.has(id)) {
      ctx.error(
        path,
        'mode-unreachable',
        `nothing carries startMode "${id}", so this mode can never run`,
      )
    }
    const endsOnShots = Array.isArray(mode['shots']) && mode['shots'].length > 0
    if (mode['timer'] === undefined && !endsOnShots && !scope.ended.has(id)) {
      ctx.error(
        path,
        'mode-cannot-end',
        `mode "${id}" has no timer, no shot set to complete and no endMode anywhere, ` +
          'so once started nothing declared here ever finishes it',
      )
    }
  }

  if (r['scoring'] !== undefined) {
    if (ctx.shape('rules.scoring', r['scoring'], [], ['bonus', 'match', 'highScore', 'replayScore'])) {
      const s = r['scoring'] as Record<string, unknown>
      if (s['replayScore'] !== undefined) ctx.num('rules.scoring.replayScore', s['replayScore'], 0, 1e15)
      // **The match block, which nothing checked at all** (M9-37). `rules.scoring`'s own shape
      // call let `match` through and no rule below it looked inside, so `digits` was unbounded and
      // untyped — and `runMatch` computed `10 ** digits` from it. A machine declaring `digits: 400`
      // produced a modulus of `Infinity`, one declaring `"two"` produced `NaN`, and both compared
      // every score against it silently for ever.
      //
      // Six is the ceiling because a match is meant to be *nearly* won: at six digits a player has
      // one chance in a hundred thousand, which is a sequence nobody ever sees resolve. One is the
      // floor because zero digits is a modulus of one and every score matches.
      const match = s['match']
      if (match !== undefined) {
        if (ctx.shape('rules.scoring.match', match, ['enabled'], ['digits', 'awardsCredit'])) {
          const m = match as Record<string, unknown>
          // Only when it is there. `shape` above already required it, and reporting *both* "the
          // field is absent" and "expected true or false, found undefined" is the cascade this
          // validator refuses everywhere else — two errors for one omission, the second of which
          // tells a reader nothing the first did not.
          if (m['enabled'] !== undefined) ctx.bool('rules.scoring.match.enabled', m['enabled'])
          if (m['digits'] !== undefined) ctx.int('rules.scoring.match.digits', m['digits'], 1, 6)
          if (m['awardsCredit'] !== undefined) {
            ctx.bool('rules.scoring.match.awardsCredit', m['awardsCredit'])
          }
        }
      }
      const bonus = s['bonus']
      if (bonus !== undefined) {
        ctx.shape('rules.scoring.bonus', bonus, [],
          ['base', 'multiplier', 'countUpSound', 'heldOnTilt', 'holdVar'])
      }
      if (isPlainObject(bonus)) {
        if (typeof bonus['holdVar'] === 'string' && !varIds.has(bonus['holdVar'])) {
          ctx.error('rules.scoring.bonus.holdVar', 'unknown-var',
            `no variable is declared with id "${bonus['holdVar']}"`)
        }
        for (const term of Array.isArray(bonus['base']) ? bonus['base'] : []) {
          if (isPlainObject(term) && typeof term['var'] === 'string' && !varIds.has(term['var'])) {
            ctx.error('rules.scoring.bonus.base', 'unknown-var', `no variable is declared with id "${term['var']}"`)
          }
        }
        const mult = bonus['multiplier']
        if (isPlainObject(mult) && typeof mult['var'] === 'string' && !varIds.has(mult['var'])) {
          ctx.error('rules.scoring.bonus.multiplier', 'unknown-var', `no variable is declared with id "${mult['var']}"`)
        }
      }
    }
  }

  return { varIds, modeIds, lampsLit: scope.lampsLit, groupsLit: scope.groupsLit }
}

function checkTrigger(
  ctx: Ctx,
  path: string,
  on: string,
  refs: RuleRefs,
  varIds: Set<string>,
  timerIds: Set<string>,
  modeIds: Set<string>,
): void {
  const parts = on.split(':')
  const prefix = parts[0]!
  if (!EVENT_PREFIXES.includes(prefix)) {
    ctx.error(path, 'unknown-trigger', `"${on}" does not start with one of: ${EVENT_PREFIXES.join(', ')}`)
    return
  }
  const name = parts[1]
  if (prefix === 'ball') {
    if (name !== 'start' && name !== 'end') {
      ctx.error(path, 'unknown-trigger', `"${on}" must be ball:start or ball:end`)
    }
    return
  }
  if (name === undefined || name.length === 0) {
    ctx.error(path, 'unknown-trigger', `"${on}" names no ${prefix}`)
    return
  }
  const known =
    prefix === 'switch' ? refs.switchIds : prefix === 'var' ? varIds : prefix === 'timer' ? timerIds : modeIds
  if (!known.has(name)) {
    ctx.error(path, `unknown-${prefix}`, `no ${prefix} is declared with id "${name}"`)
  }
}

function validateSettings(ctx: Ctx, settings: unknown): void {
  if (!isPlainObject(settings)) {
    ctx.error('settings', 'not-an-object', `expected a settings object, found ${describe(settings)}`)
    return
  }
  for (const key of Object.keys(settings).sort()) {
    const path = `settings.${key}`
    const s = settings[key]
    if (!isPlainObject(s)) {
      ctx.error(path, 'not-an-object', `expected a setting object, found ${describe(s)}`)
      continue
    }
    if (!('default' in s)) {
      ctx.error(path, 'missing-field', 'a setting needs a default')
      continue
    }
    if (!ctx.shape(path, s, ['default'], ['range', 'options'])) continue
    const def = s['default']
    if (Array.isArray(s['range'])) {
      const range = s['range']
      if (range.length !== 2 || typeof range[0] !== 'number' || typeof range[1] !== 'number') {
        ctx.error(`${path}.range`, 'not-a-range', 'range must be [min, max]')
      } else if (typeof def === 'number' && (def < range[0] || def > range[1])) {
        ctx.error(`${path}.default`, 'out-of-range', `default ${def} is outside its own range ${range[0]}…${range[1]}`)
      }
    }
    if (Array.isArray(s['options'])) {
      if (typeof def === 'string' && !s['options'].includes(def)) {
        ctx.error(`${path}.default`, 'not-in-options', `default "${def}" is not among its own options`)
      }
    }
  }
}

/** Throwing wrapper for callers that treat an invalid definition as unrecoverable. */
export function assertValidMachine(doc: unknown): MachineDefinition {
  const report = validateMachine(doc)
  if (!report.ok) {
    const shown = report.errors.slice(0, 8).map((e) => `  ${e.path || '<root>'}: ${e.message}`)
    const more = report.errors.length > shown.length ? `\n  …and ${report.errors.length - shown.length} more` : ''
    throw new Error(`machine definition is invalid (${report.errors.length} error(s)):\n${shown.join('\n')}${more}`)
  }
  return doc as MachineDefinition
}
